// What happened at each door, on the console that sent the driver.
//
// The board answers "which rounds need me?" across the day. This answers
// "what happened on *this* one?" for the job in front of the operator,
// and it is the surface a single-driver operation lives on — the board
// does not exist below rung 2, and a takeaway with one rider still needs
// to know the parcel arrived.
//
// Every line is the run's own signed record, read through the same
// honesty model the customer's page uses. Two rules hold this file to
// that and are worth stating, because both are easy to break by being
// helpful:
//
// - **Pending is not "on the way".** An outcome is asserted and never
//   inferred (§5.2.1), so a stop nobody has spoken about says nobody has
//   spoken about it. The van being past it on the map is not a delivery.
// - **A photo is fetched, never assumed.** The commitment is public and
//   signed, and the blob behind it is pulled from whoever still holds
//   it and checked against that commitment before anyone sees it. This
//   console can open one because the run seed is inside the ticket it
//   sealed — a customer holding the link has the same commitment and
//   cannot, which is §20.7's trade rather than an omission.

import {
  STOP_OUTCOME,
  stopProgress,
  THREAD_STATE,
  type FollowHandle,
  type FollowSnapshot,
  type StopOutcomeCode
} from "@wayfind/core";
import { useCallback, useEffect, useState } from "react";
import { Note, Panel } from "./Bits";
import type { Dispatcher } from "../ops/dispatch";
import { plural, type Vocabulary } from "../ops/words";
import type { FleetState, StoredJob } from "../ops/state";

/**
 * How a driver's word about one stop should read on a dispatcher's
 * screen. "Nothing said yet" rather than "pending": pending is the
 * protocol's word for the absence of an assertion, and a dispatcher
 * reading a board should be told that nobody has spoken rather than
 * given a status that sounds like one.
 */
function outcomeWords(outcome: StopOutcomeCode): {
  label: string;
  tone: "settled" | "halt" | "quiet";
} {
  switch (outcome) {
    case STOP_OUTCOME.DELIVERED:
      return { label: "Delivered", tone: "settled" };
    case STOP_OUTCOME.SKIPPED:
      return { label: "Skipped", tone: "halt" };
    case STOP_OUTCOME.FAILED:
      return { label: "Not delivered", tone: "halt" };
    default:
      return { label: "Nothing said yet", tone: "quiet" };
  }
}

export function DeliveryProgress({
  job,
  snapshot,
  vocabulary,
  state,
  dispatcher,
  proof
}: {
  job: StoredJob;
  /** Null while the console has not opened a follow for this job at all. */
  snapshot: FollowSnapshot | null;
  vocabulary: Vocabulary;
  state: FleetState;
  dispatcher: Dispatcher | null;
  /** The follow's §20.7 half, when this console is following the run. */
  proof: FollowHandle["proof"] | null;
}): JSX.Element {
  const status = snapshot?.status ?? null;
  const heard = status?.observed.ever ?? false;
  const progress = status ? stopProgress(status, snapshot?.plan ?? null) : null;
  const finished = status
    ? status.state === THREAD_STATE.COMPLETED || status.state === THREAD_STATE.CANCELED
    : false;

  return (
    <Panel eyebrow="What the run says">
      {!heard ? (
        <Note
          title={
            job.notAfter > 0 && job.notAfter * 1000 <= Date.now()
              ? "This job has expired."
              : `Nothing has been published about this ${vocabulary.job} yet.`
          }
          tone="quiet"
        >
          {job.notAfter > 0 && job.notAfter * 1000 <= Date.now()
            ? "The capability behind it is over, so nothing can be published onto it and nothing "
              + "can be read from it — including by whoever holds the link."
            : `A run starts publishing when the ${vocabulary.driver} takes the job on their phone. `
              + "Until then there is nothing to read: this console is subscribed and listening, and "
              + "silence here means silence on the wire rather than a screen that has not refreshed."}
        </Note>
      ) : (
        <>
          <p className="subtitle" style={{ marginTop: 0 }}>
            {progress
              ? `${progress.delivered} of ${plural(progress.total, vocabulary.stop)} delivered`
              : ""}
            {progress?.skipped ? ` · ${progress.skipped} skipped` : ""}
            {progress?.failed ? ` · ${progress.failed} not delivered` : ""}
            {finished ? " · the run has ended" : ""}
          </p>

          <div className="rows" style={{ marginTop: "0.6rem" }}>
            {job.stops.map((stop, index) => {
              // The outcome map is keyed by position in the plan, 1-based,
              // and this list is the plan — so the index is the key. A stop
              // the map does not reach yet is pending, which is the honest
              // reading of a shorter map rather than a missing one.
              const outcome = (status?.outcomes[index] ?? STOP_OUTCOME.PENDING) as StopOutcomeCode;
              const words = outcomeWords(outcome);
              const isLatest = status?.lastOutcome?.stopIndex === index + 1;
              return (
                <div key={stop.id} className="rowcard">
                  <div className="rowcard__main">
                    <span className="rowcard__name">{index + 1}. {stop.label}</span>
                    {/*
                      The chip carries the outcome, so this line carries
                      only what the chip cannot: the proof, when the
                      driver took one. Saying the outcome twice reads as
                      two facts.
                    */}
                    {/*
                      Offered for any settled stop once the run has
                      published a photo chain — not only for the newest
                      mark. The wire's `hasPhoto` describes the *latest*
                      outcome and nothing else, so gating on it hid the
                      photo from the first doorstep the moment the second
                      was delivered. §20.7.1's accumulator is what makes
                      the older ones recoverable, and the key is scoped
                      per stop, so only this door's photo can open here.
                    */}
                    {settledOutcome(outcome) && status?.photoChain ? (
                      <Proof
                        job={job}
                        state={state}
                        dispatcher={dispatcher}
                        proof={proof}
                        stopIndex={index + 1}
                        chainHead={status.photoChain}
                        known={isLatest && status.lastOutcome?.hasPhoto === true}
                      />
                    ) : null}
                  </div>
                  <span className={`chip${words.tone === "quiet" ? "" : ` chip--${words.tone}`}`}>
                    <span className="chip__dot" aria-hidden="true" />
                    {words.label}
                  </span>
                </div>
              );
            })}
          </div>

          <hr className="divider" />
          <p className="small muted" style={{ margin: 0 }}>
            {status?.observed.ageSeconds != null
              ? `Last heard ${status.observed.ageSeconds < 60
                ? "just now"
                : `${Math.round(status.observed.ageSeconds / 60)} min ago`}`
              : "Last heard: unknown"}
            {status?.position.kind === "withheld"
              ? ` · this run publishes ${vocabulary.stop} events only, no position`
              : ""}
            {status?.claim ? ` · ${status.claim}` : ""}
          </p>
        </>
      )}
    </Panel>
  );
}

/** A stop somebody has spoken about, which is the only kind with proof. */
function settledOutcome(outcome: StopOutcomeCode): boolean {
  return outcome === STOP_OUTCOME.DELIVERED
    || outcome === STOP_OUTCOME.SKIPPED
    || outcome === STOP_OUTCOME.FAILED;
}

/**
 * The doorstep photo behind a mark (§20.7), fetched and opened here.
 *
 * Three parties and three different powers, and this component is the
 * only place all three are visible at once: the driver sealed it, every
 * link-holder can see *that* it exists and check the commitment, and
 * only a holder of the run seed can open it. This console holds one
 * because every job is sealed to it as well as to the phone — which is
 * the reason the interface has always claimed a dispatcher can open a
 * doorstep photo, and now the reason it can.
 *
 * Fetched on request rather than eagerly. A round of twelve deliveries
 * is twelve blobs pulled off other people's phones, and a dispatcher
 * looking at a board wants none of them until a customer disputes one.
 */
function Proof({
  job,
  state,
  dispatcher,
  proof,
  stopIndex,
  chainHead,
  known
}: {
  job: StoredJob;
  state: FleetState;
  dispatcher: Dispatcher | null;
  proof: FollowHandle["proof"] | null;
  stopIndex: number;
  chainHead: string | null;
  /**
   * Whether the run *said* there is a photo at this door, rather than
   * this being one of the stops a photo could exist for. The wire only
   * states it for the newest mark, so the wording has to differ: a
   * promise for one, an offer to look for the other.
   */
  known: boolean;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  // An object URL is a live handle on bytes the browser is holding. It
  // has to be given back, or a dispatcher who opens a day's proofs leaks
  // every one of them until the tab closes.
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const open = useCallback(async () => {
    if (!dispatcher || !proof) return;
    setBusy(true);
    setProblem(null);
    try {
      // §20.7.1: ask the run which commitments it published and take
      // only the ones its own signed accumulator vouches for. Fetching
      // on an unverified hash would be fetching on somebody's say-so.
      const list = await proof.list(chainHead);
      // Each entry names its own door, so this takes the one for this
      // stop rather than trying keys until one decrypts. A door can be
      // photographed more than once — a re-attempt after a failed
      // delivery — so the newest wins, which is the last one the chain
      // bound.
      const mine = list.verified.filter(entry => entry.stopIndex === stopIndex);
      const entry = mine[mine.length - 1] ?? null;
      if (!entry) {
        setProblem(
          list.verified.length
            ? "This run published proof for other stops, and none for this one."
            : "The run has published no photo commitments this console can verify yet."
        );
        return;
      }
      const sealed = await proof.fetch(entry.commitment);
      if (!sealed) {
        setProblem(
          "Nobody reachable is holding those bytes. The commitment is on the signed record, so "
          + "the photo exists — the phone that took it has to be on the mesh to hand it over."
        );
        return;
      }
      const opened = await dispatcher.openProofPhoto({
        sealedB64: job.sealedB64,
        consolePrivateKeyHex: state.consoleDevice.privateKeyHex,
        sealed,
        stopIndex,
        hash: entry.commitment
      });
      setUrl(URL.createObjectURL(new Blob([opened.bytes as BlobPart], { type: opened.type })));
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, [dispatcher, proof, chainHead, job.sealedB64, state.consoleDevice.privateKeyHex, stopIndex]);

  if (url) {
    return (
      <span className="rowcard__sub">
        <img
          src={url}
          alt="The photo taken at this door"
          style={{ display: "block", maxWidth: "100%", borderRadius: "var(--radius-sm)", marginTop: "0.35rem" }}
        />
        Opened here with the run seed from this job’s own ticket. The bytes matched the commitment
        the {job.driverName ? job.driverName : "driver"} signed.
      </span>
    );
  }

  return (
    <span className="rowcard__sub">
      {known ? "A photo was taken at the door" : "Proof of delivery"}
      {dispatcher && proof && job.sealedB64 ? (
        <>
          {" · "}
          <button
            type="button"
            className="button button--small button--ghost"
            disabled={busy}
            onClick={() => { void open(); }}
          >
            {busy ? "Fetching…" : known ? "Show it" : "Look for a photo"}
          </button>
        </>
      ) : null}
      {problem ? <span className="small muted" style={{ display: "block" }}>{problem}</span> : null}
    </span>
  );
}
